import { useEffect, useMemo, useRef, useState } from "react";
import { useStateValue } from "../../context/StateProvider";
import { formatDate, formatNumberShort } from "../../utils/helper";
import { dealCreditsAgent } from "../../utils/dealCredit";
import YearFilterDropdown from "./YearFilterDropdown";
import StatDetails from "./../deals/StatDetails";
import {
  Target,
  TrendingDown,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  BarChart3,
  Clock,
  Home,
  ArrowUp,
  Activity,
  Filter,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTH_NAMES_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Median of a list of numbers; null if the list is empty.
function median(nums) {
  const xs = nums.filter((n) => Number.isFinite(n)).slice().sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

// Initials for the identity avatar.
function getInitials(user) {
  if (!user) return "?";
  const f = (user.firstname || "").trim();
  const l = (user.lastname || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (f || l || "?").slice(0, 2).toUpperCase();
}

// Deterministic avatar gradient based on user id, matching the mockup's variety.
const AVATAR_GRADIENTS = [
  "bg-[#1E293B]",
  "bg-gradient-to-br from-blue-900 to-blue-500",
  "bg-gradient-to-br from-amber-800 to-amber-500",
  "bg-gradient-to-br from-rose-700 to-rose-500",
  "bg-gradient-to-br from-green-800 to-green-500",
  "bg-gradient-to-br from-violet-800 to-violet-400",
];
function getAvatarClass(user) {
  if (!user) return AVATAR_GRADIENTS[0];
  const seed = String(user.id || "").charCodeAt(0) || 0;
  return AVATAR_GRADIENTS[seed % AVATAR_GRADIENTS.length];
}

function TeamPerformanceDetail({
  id,
  childSelectedYear,
  isWidth = true,
  isShowMonthly = true,
  dashboardview,
  teamView = false,
  hideHeader = false, // hide the navy page header when embedding on dashboard
}) {
  const [{ user, users, deals, leadsource, listingAppointments }] =
    useStateValue();
  const [, setSelectedMonth] = useState(null);
  const [statspopup, setstatspopup] = useState(false);
  const [selectedstat, setselectedstat] = useState("");
  const [statsdata, setstatsdata] = useState([]);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [filterYear, setFilterYear] = useState(String(currentYear));
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);
  // When true, the "On Pace" calc includes pending deal volume — gives a more
  // optimistic projection. Default is closed-only (the conservative view).
  const [includePending, setIncludePending] = useState(false);
  const [agentDropdownVisible, setAgentDropdownVisible] = useState(false);
  const navigate = useNavigate();

  const today = new Date();
  const selectedUser = teamView
    ? null
    : users.find((u) => String(u.id) === String(id));

  // Active producers for team-wide aggregations and roster display.
  // Includes only Admins (Team Leads) and Employees (Agents).
  // Excludes deleted (type 1), disabled (type 2), accountants, and supervisors.
  const teamMembers = useMemo(
    () =>
      (users || []).filter(
        (u) =>
          u?.type !== 1 &&
          u?.type !== 2 &&
          (u?.role === "Admin" || u?.role === "Employee"),
      ),
    [users],
  );

  useEffect(() => {
    setSelectedMonth((prev) =>
      prev
        ? { month: prev.month, year: Number(filterYear) }
        : { month: currentMonth, year: Number(filterYear) },
    );
    childSelectedYear && childSelectedYear(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // ── GOALS ──
  // Goals live on three legacy arrays per user:
  //   yearlyGoals[].amount             → GCI ($)
  //   closedvolumeyearlygoals[].amount → Volume ($)
  //   closedyearlygoals[].count        → Units (#)
  // Assemble a unified yearGoal object from those.
  const yearGoal = useMemo(() => {
    const goalsFromUser = (u) => {
      if (!u) return { amount: 0, volumeGoal: 0, unitsGoal: 0, hasAny: false };
      const gci = Number(
        u.yearlyGoals?.find((g) => g.year === selectedYear)?.amount || 0,
      );
      const volume = Number(
        u.closedvolumeyearlygoals?.find((g) => g.year === selectedYear)
          ?.amount || 0,
      );
      const units = Number(
        u.closedyearlygoals?.find((g) => g.year === selectedYear)?.count || 0,
      );
      return {
        amount: gci,
        volumeGoal: volume,
        unitsGoal: units,
        hasAny: gci > 0 || volume > 0 || units > 0,
      };
    };

    if (teamView) {
      const totals = teamMembers.reduce(
        (acc, u) => {
          const g = goalsFromUser(u);
          acc.amount += g.amount;
          acc.volumeGoal += g.volumeGoal;
          acc.unitsGoal += g.unitsGoal;
          if (g.hasAny) acc.hasAny = true;
          return acc;
        },
        {
          year: selectedYear,
          amount: 0,
          volumeGoal: 0,
          unitsGoal: 0,
          hasAny: false,
        },
      );
      return totals.hasAny ? totals : null;
    }
    const src = dashboardview ? user : selectedUser;
    const g = goalsFromUser(src);
    return g.hasAny ? { year: selectedYear, ...g } : null;
  }, [teamView, teamMembers, selectedUser, user, selectedYear, dashboardview]);

  // Role-aware filter shared by every deal computation below.
  const matchesSelectedUser = (d) => {
    if (teamView) return true; // Team view: include every deal.
    if (!selectedUser) return false;
    if (selectedUser.role === "Admin") {
      return (
        d?.agentData === selectedUser.id ||
        (d?.splittype === "NoSplit" &&
          d?.assignedteamlead === selectedUser.id) ||
        (d?.splittype === "Referral" && d?.agentData === "") ||
        (d?.splittype === "TeamSplit" && d?.inhousereferral === true)
      );
    }
    return d?.agentData === selectedUser.id && d?.inhousereferral !== true;
  };

  // Commission for a single deal.
  // Team view → team's take-home; agent view → that agent's share.
  const commissionFor = (d) => {
    if (teamView) return Number(d.takehomecommissionamount || 0);
    if (selectedUser?.role === "Admin") {
      return d.assignedteamlead === id
        ? Number(d.teamleadCommissionamount || 0)
        : Number(d.agentCommissionamount || 0);
    }
    return Number(d.agentCommissionamount || 0);
  };

  // ── UPCOMING TEAM CLOSINGS (Admin / Accountant) ──
  const upcomingTeamDeals = useMemo(() => {
    if (!deals || !selectedUser) return [];
    return deals
      .filter((d) => {
        const stage = d.dealStage?.toLowerCase();
        if (!d.closingdate) return false;
        if (stage !== "pending") return false;
        if (selectedUser?.role !== "Admin" && selectedUser?.role !== "Accountant")
          return false;
        return (
          (d?.splittype === "TeamSplit" &&
            d.agentData !== selectedUser.id &&
            d?.inhousereferral === false) ||
          (d?.splittype === "Referral" && d.agentData !== selectedUser.id) ||
          (d?.splittype === "NoSplit" &&
            d.assignedteamlead !== selectedUser.id)
        );
      })
      .sort((a, b) => new Date(a.closingdate) - new Date(b.closingdate));
  }, [deals, selectedUser]);

  const upcomingTeamCommission = upcomingTeamDeals.reduce(
    (s, d) => s + Number(d.takehomecommissionamount || 0),
    0,
  );

  // ── UPCOMING CLOSINGS (for this user, or team-wide if teamView) ──
  const upcomingDeals = useMemo(() => {
    if (!deals) return [];
    if (teamView) {
      return deals
        .filter((d) => {
          const stage = d.dealStage?.toLowerCase();
          return d.closingdate && stage === "pending";
        })
        .sort((a, b) => new Date(a.closingdate) - new Date(b.closingdate));
    }
    if (!selectedUser) return [];
    return deals
      .filter((d) => {
        const stage = d.dealStage?.toLowerCase();
        if (!d.closingdate) return false;
        if (stage !== "pending") return false;
        if (selectedUser?.role === "Admin") {
          return (
            d?.agentData === selectedUser.id ||
            (d?.splittype === "NoSplit" &&
              d?.assignedteamlead === selectedUser.id) ||
            (d?.splittype === "Referral" &&
              d?.agentData === "" &&
              d?.assignedteamlead === selectedUser.id) ||
            (d?.splittype === "TeamSplit" &&
              d?.inhousereferral === true &&
              d?.assignedteamlead === selectedUser.id)
          );
        }
        return d.agentData === selectedUser.id;
      })
      .sort((a, b) => new Date(a.closingdate) - new Date(b.closingdate));
  }, [deals, selectedUser, teamView]);

  const upcomingCommission = upcomingDeals.reduce(
    (s, d) =>
      s +
      (teamView
        ? Number(d.takehomecommissionamount || 0)
        : selectedUser?.id === d?.assignedteamlead
          ? Number(d.teamleadCommissionamount || 0)
          : Number(d.agentCommissionamount || 0)),
    0,
  );

  // Master list of relevant deals.
  // Team view → every deal with a closingdate; agent view → that agent's deals.
  const matchedDeals = teamView
    ? deals.filter((d) => d.closingdate)
    : deals.filter((deal) => {
        if (selectedUser?.role === "Admin")
          return (
            deal.assignedteamlead === selectedUser.id ||
            deal.agentData === selectedUser.id
          );
        return deal.agentData === selectedUser?.id;
      });

  // Build monthly buckets for the selected year.
  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const year = selectedYear;
    const monthlyDeals = matchedDeals.filter((d) => {
      const dd = new Date(d.closingdate);
      return dd.getFullYear() === year && dd.getMonth() + 1 === month;
    });

    const closedDeals = monthlyDeals.filter(
      (d) =>
        d.dealStage?.toLowerCase() === "closed" && matchesSelectedUser(d),
    );
    const pendingDeals = monthlyDeals.filter(
      (d) =>
        !["closed", "cancelled", "expired"].includes(
          d.dealStage?.toLowerCase(),
        ) && matchesSelectedUser(d),
    );

    const closed = closedDeals.reduce((sum, d) => sum + commissionFor(d), 0);
    const pending = pendingDeals.reduce((sum, d) => sum + commissionFor(d), 0);

    const monthlyvolume = closedDeals.reduce(
      (sum, d) => sum + Number(d.offerPrice || 0),
      0,
    );
    const monthlypendingvolume = pendingDeals.reduce(
      (sum, d) => sum + Number(d.offerPrice || 0),
      0,
    );

    return {
      month,
      year,
      closedCommission: closed,
      pendingCommission: pending,
      netCommission: closed + pending,
      closedDealsCount: closedDeals.length,
      closedDeals,
      PendingDealsCount: pendingDeals.length,
      PendingDeals: pendingDeals,
      monthlyvolume,
      monthlypendingvolume,
    };
  });

  // ── YTD AGGREGATES ──
  const PendingDealsCount = matchedDeals.filter(
    (d) =>
      !["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase()) &&
      new Date(d.closingdate).getFullYear() === selectedYear &&
      matchesSelectedUser(d),
  );
  const AllDealsCount = matchedDeals.filter(
    (d) =>
      new Date(d.closingdate).getFullYear() === selectedYear &&
      matchesSelectedUser(d),
  );
  const closedDealsCount = matchedDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "closed" &&
      new Date(d.closingdate).getFullYear() === selectedYear &&
      matchesSelectedUser(d),
  );

  const ytdVolume = closedDealsCount.reduce(
    (sum, d) => sum + Number(d.offerPrice || 0),
    0,
  );
  const ytdPendingVolume = PendingDealsCount.reduce(
    (sum, d) => sum + Number(d.offerPrice || 0),
    0,
  );
  const ytdnetVolume = ytdVolume + ytdPendingVolume;

  const closedCommissionTotal = monthlyData.reduce(
    (s, m) => s + m.closedCommission,
    0,
  );

  const pendingDealsYTD = matchedDeals.filter((d) => {
    if (!d.closingdate) return false;
    if (new Date(d.closingdate).getFullYear() !== selectedYear) return false;
    if (
      ["closed", "cancelled", "expired"].includes(d.dealStage?.toLowerCase())
    )
      return false;
    if (teamView) return true;
    if (selectedUser?.role === "Admin") {
      return (
        d.assignedteamlead === selectedUser.id ||
        d.agentData === selectedUser.id
      );
    }
    return d.agentData === selectedUser.id;
  });
  const closedDealsYTD = matchedDeals.filter(
    (d) =>
      d.closingdate &&
      new Date(d.closingdate).getFullYear() === selectedYear &&
      d.dealStage?.toLowerCase() === "closed",
  );
  const pendingCommissionTotal = pendingDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );
  const netCommissionDealsYTD = [...closedDealsYTD, ...pendingDealsYTD];
  const netCommissionTotal = netCommissionDealsYTD.reduce(
    (sum, d) => sum + commissionFor(d),
    0,
  );

  // ── PRIOR YEAR (for YoY deltas) ──
  const priorYear = selectedYear - 1;
  const priorYearClosedDeals = matchedDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "closed" &&
      new Date(d.closingdate).getFullYear() === priorYear &&
      matchesSelectedUser(d),
  );
  const priorYearVolume = priorYearClosedDeals.reduce(
    (s, d) => s + Number(d.offerPrice || 0),
    0,
  );
  // yoyVolumePct is shown next to the big YTD Volume number — it uses
  // the same display value (closed, or closed + pending when the toggle
  // is on) so the headline number and its YoY badge update together.
  // Inline the include-pending sum here because displayVolume is declared
  // further down the function body.
  const yoyHeadlineVolume = includePending
    ? ytdVolume + ytdPendingVolume
    : ytdVolume;
  const yoyVolumePct =
    priorYearVolume > 0
      ? ((yoyHeadlineVolume - priorYearVolume) / priorYearVolume) * 100
      : null;

  // Same-period prior year (Jan 1 → today's month/day) for apples-to-apples YoY.
  const cutoffMonth = today.getMonth();
  const cutoffDay = today.getDate();
  const withinSamePeriod = (dt) =>
    dt.getMonth() < cutoffMonth ||
    (dt.getMonth() === cutoffMonth && dt.getDate() <= cutoffDay);
  const priorSamePeriodClosed = priorYearClosedDeals.filter((d) =>
    withinSamePeriod(new Date(d.closingdate)),
  );
  const priorSamePeriodGci = priorSamePeriodClosed.reduce(
    (s, d) => s + commissionFor(d),
    0,
  );
  const priorSamePeriodVolume = priorSamePeriodClosed.reduce(
    (s, d) => s + Number(d.offerPrice || 0),
    0,
  );
  const priorSamePeriodUnits = priorSamePeriodClosed.length;
  const priorSamePeriodAvg =
    priorSamePeriodUnits > 0 ? priorSamePeriodVolume / priorSamePeriodUnits : 0;

  // YTD this year for each metric.
  const ytdGci = closedCommissionTotal;
  const ytdUnits = closedDealsCount.length;
  const ytdAvg = ytdUnits > 0 ? ytdVolume / ytdUnits : 0;

  // Display values that swap based on the "Include pending" toggle. When
  // ON, the tiles that aren't explicitly labeled "Closed" / "Pending" /
  // "Net" (YTD Volume headline, the Closed Deals count, GCI / Volume /
  // Units / Avg Sale Price in the YoY row, and the pace badge) reflect
  // closed + pending. Tiles that are explicitly named ("Closed
  // Commission", "Pending Commission", "YTD Pending Volume", "Net YTD
  // Volume", "Net Commission") stay locked to their named meaning so
  // their labels never lie.
  const displayVolume = includePending ? ytdnetVolume : ytdVolume;
  const displayUnits = includePending
    ? ytdUnits + PendingDealsCount.length
    : ytdUnits;
  const displayGci = includePending
    ? closedCommissionTotal + pendingCommissionTotal
    : ytdGci;
  const displayAvg = displayUnits > 0 ? displayVolume / displayUnits : 0;

  // Same-period YoY % deltas (null when prior had no data). The "display"
  // versions use the toggle-aware value so when "Include pending" is on,
  // the headline tile shows "$3.65M ↑X% YoY" reflecting closed+pending vs
  // prior same-period closed. The "closed-only" versions are used on
  // tiles whose value never includes pending (e.g. the hero's Closed
  // Commission badge) so the badge never lies about its own number.
  const samePeriodYoy = (curr, prior) =>
    prior > 0 ? ((curr - prior) / prior) * 100 : null;
  const closedGciYoy = samePeriodYoy(ytdGci, priorSamePeriodGci);
  const gciYoy = samePeriodYoy(displayGci, priorSamePeriodGci);
  const volumeYoy = samePeriodYoy(displayVolume, priorSamePeriodVolume);
  const unitsYoy = samePeriodYoy(displayUnits, priorSamePeriodUnits);
  const avgYoy = samePeriodYoy(displayAvg, priorSamePeriodAvg);

  // Prior-year monthly closed commission (for chart YoY trend line).
  const priorYearMonthly = Array.from({ length: 12 }, (_, i) => {
    const monthDeals = matchedDeals.filter((d) => {
      const dd = new Date(d.closingdate);
      return (
        dd.getFullYear() === priorYear &&
        dd.getMonth() === i &&
        d.dealStage?.toLowerCase() === "closed" &&
        matchesSelectedUser(d)
      );
    });
    return monthDeals.reduce((s, d) => s + commissionFor(d), 0);
  });

  // ── GOAL PACING ──
  const gciGoal = yearGoal ? Number(yearGoal.amount || 0) : 0;
  const volumeGoal = yearGoal ? Number(yearGoal.volumeGoal || 0) : 0;
  const unitsGoal = yearGoal ? Number(yearGoal.unitsGoal || 0) : 0;
  const TotalPct =
    gciGoal > 0 ? ((closedCommissionTotal / gciGoal) * 100).toFixed(1) : 0;
  // Goal-pace % follows the display value: when Include Pending is on, the
  // "X% of goal" pill on each tile reflects closed + pending so it tracks
  // the headline number it's annotating. closed* variants stay locked to
  // closed-only for tiles whose value never includes pending (the hero's
  // Closed Commission card).
  const closedGciPacePct = gciGoal > 0 ? (ytdGci / gciGoal) * 100 : null;
  const gciPacePct = gciGoal > 0 ? (displayGci / gciGoal) * 100 : null;
  const unitsPacePct =
    unitsGoal > 0 ? (displayUnits / unitsGoal) * 100 : null;

  const monthsElapsed = currentMonth + today.getDate() / 30;
  const expectedAtNow = (monthsElapsed / 12) * (volumeGoal || 0);
  const volumePacePct =
    volumeGoal > 0 ? (displayVolume / volumeGoal) * 100 : 0;
  const expectedPacePct =
    volumeGoal > 0 ? (expectedAtNow / volumeGoal) * 100 : 0;
  // Pace volume — closed only, or closed + pending when the toggle is on.
  const paceVolume = includePending
    ? ytdVolume + ytdPendingVolume
    : ytdVolume;
  const runRatePerMonth = monthsElapsed > 0 ? paceVolume / monthsElapsed : 0;
  const projectedYearEnd = runRatePerMonth * 12;
  const projectedPct =
    volumeGoal > 0 ? (projectedYearEnd / volumeGoal) * 100 : 0;
  const monthsRemaining = Math.max(0, 12 - monthsElapsed);
  const needPerMonth =
    monthsRemaining > 0
      ? Math.max(0, (volumeGoal - paceVolume) / monthsRemaining)
      : 0;
  const endOfYear = new Date(selectedYear, 11, 31);
  const daysRemaining = Math.max(
    0,
    Math.ceil((endOfYear - today) / (1000 * 60 * 60 * 24)),
  );
  const isOnPace = projectedPct >= 95;

  // ── PIPELINE DISTRIBUTION (real data — open deals only) ──
  const PIPELINE_STAGES = [
    { key: "cma", label: "CMA's", color: "#94a3b8" },
    { key: "listing appt", label: "Listing Appts", color: "#94a3b8" },
    { key: "pre-market", label: "Pre-Market", color: "#64748b" },
    { key: "coming soon", label: "Coming Soon", color: "#64748b" },
    { key: "active", label: "Active", color: "#1E293B" },
    { key: "under contract", label: "Under Contract", color: "#1d4ed8" },
    { key: "pending", label: "Pending", color: "#b45309" },
    { key: "on hold", label: "On Hold", color: "#94a3b8" },
  ];
  const pipelineRows = PIPELINE_STAGES.map((stage) => {
    const dealsInStage = matchedDeals.filter(
      (d) =>
        d.dealStage?.toLowerCase() === stage.key &&
        new Date(d.closingdate).getFullYear() === selectedYear &&
        matchesSelectedUser(d),
    );
    const volume = dealsInStage.reduce(
      (s, d) => s + Number(d.offerPrice || 0),
      0,
    );
    return { ...stage, count: dealsInStage.length, volume };
  });
  const maxPipelineCount = Math.max(1, ...pipelineRows.map((r) => r.count));
  const openPipelineVolume = pipelineRows.reduce((s, r) => s + r.volume, 0);
  const openPipelineCount = pipelineRows.reduce((s, r) => s + r.count, 0);

  // Active / Coming Soon / Pre-Market listings for this user — actual deal
  // records, not just counts. Used by the Active Listings panel below.
  const activeListings = matchedDeals.filter(
    (d) => d.dealStage?.toLowerCase() === "active" && matchesSelectedUser(d),
  );
  const comingSoonListings = matchedDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "coming soon" && matchesSelectedUser(d),
  );
  const preMarketListings = matchedDeals.filter(
    (d) =>
      d.dealStage?.toLowerCase() === "pre-market" && matchesSelectedUser(d),
  );
  const expectedOpenGci = pendingCommissionTotal;

  // ── CYCLE TIME ANALYSIS ──
  // Scope listingAppointments to the selected year + selected user (Admins see
  // all in team view or their own role-scoped set; Employees see their own).
  const scopedAppts = useMemo(() => {
    const all = listingAppointments || [];
    return all.filter((a) => {
      const apptYr = a.appointmentDate
        ? new Date(a.appointmentDate).getFullYear()
        : Number(a.forYear);
      if (apptYr !== selectedYear) return false;
      if (teamView) return true;
      if (!selectedUser) return false;
      if (selectedUser.role === "Admin") return true;
      return a.agentId === selectedUser.id;
    });
  }, [listingAppointments, selectedYear, teamView, selectedUser]);

  // Lead → Appointment: days between record creation and the appointment date.
  // Only count appointments where both dates exist and createdAt precedes
  // appointmentDate (filters out after-the-fact data entry).
  const cycleLeadToAppt = useMemo(() => {
    const days = scopedAppts
      .map((a) => {
        const created = a.createdAt ? new Date(a.createdAt) : null;
        const appt = a.appointmentDate ? new Date(a.appointmentDate) : null;
        if (!created || !appt) return null;
        const diff = (appt - created) / 86400000;
        return diff >= 0 ? diff : null;
      })
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
  }, [scopedAppts]);

  // Lead → Close (Full): for closed deals in scope/year, days from deal
  // createdat to closingdate.
  const cycleLeadToClose = useMemo(() => {
    const days = closedDealsCount
      .map((d) => {
        const created = d.createdat ? new Date(Number(d.createdat)) : null;
        const closed = d.closingdate ? new Date(d.closingdate) : null;
        if (!created || !closed || isNaN(created) || isNaN(closed)) return null;
        const diff = (closed - created) / 86400000;
        return diff >= 0 ? diff : null;
      })
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
  }, [closedDealsCount]);

  // Helper: per-deal day-diff between two date-ish fields.
  const dayDiff = (start, end) => {
    if (!start || !end) return null;
    const s = new Date(typeof start === "number" ? start : start);
    const e = new Date(typeof end === "number" ? end : end);
    if (isNaN(s) || isNaN(e)) return null;
    const diff = (e - s) / 86400000;
    return diff >= 0 ? diff : null;
  };

  // Listing → Under Contract: listedDate → underContractDate. Requires both
  // stamps (only populated for deals that have flipped through those stages
  // since the timestamp tracking was added).
  const cycleListToUc = useMemo(() => {
    const sellerInYear = matchedDeals.filter((d) => {
      if (d.dealtype !== "Seller") return false;
      if (!matchesSelectedUser(d)) return false;
      const yr = d.closingdate
        ? new Date(d.closingdate).getFullYear()
        : null;
      return yr === selectedYear;
    });
    const days = sellerInYear
      .map((d) => dayDiff(d.listedDate, d.underContractDate))
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedDeals, selectedYear, selectedUser, teamView]);

  // Contract → Close: underContractDate → closedDate.
  const cycleUcToClose = useMemo(() => {
    const days = closedDealsCount
      .map((d) => dayDiff(d.underContractDate, d.closedDate || d.closingdate))
      .filter((n) => n !== null);
    return { median: median(days), sample: days.length };
  }, [closedDealsCount]);

  // ── CONVERSION FUNNEL (seller-side) ──
  const funnel = useMemo(() => {
    const cmaCount = scopedAppts.length;
    const listedCount = scopedAppts.filter(
      (a) => a.status === "won" || a.status === "closed",
    ).length;

    // UC = seller-side deals at Under Contract / Pending / Closed in the year.
    const sellerDealsInYear = matchedDeals.filter((d) => {
      if (d.dealtype !== "Seller") return false;
      if (!matchesSelectedUser(d)) return false;
      const yr = d.closingdate
        ? new Date(d.closingdate).getFullYear()
        : null;
      return yr === selectedYear;
    });
    const ucCount = sellerDealsInYear.filter((d) =>
      ["under contract", "pending", "closed"].includes(
        (d.dealStage || "").toLowerCase(),
      ),
    ).length;
    const closedSellerCount = sellerDealsInYear.filter(
      (d) => (d.dealStage || "").toLowerCase() === "closed",
    ).length;

    const pct = (num, den) =>
      den > 0 ? `${Math.round((num / den) * 100)}%` : "—";

    return {
      cma: cmaCount,
      listed: listedCount,
      uc: ucCount,
      closed: closedSellerCount,
      cmaToList: pct(listedCount, cmaCount),
      listToUc: pct(ucCount, listedCount),
      ucToClosed: pct(closedSellerCount, ucCount),
      overall: pct(closedSellerCount, cmaCount),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedAppts, matchedDeals, selectedYear, selectedUser, teamView]);

  // ── LEAD SOURCES ──
  const leadSourceData = useMemo(() => {
    if (!leadsource || !closedDealsCount) return [];
    return leadsource
      .map((source) => {
        const matched = closedDealsCount.filter(
          (deal) => deal?.leadsource?.abbreviation === source?.abbreviation,
        );
        return {
          leadsourceId: source?.id,
          leadsourceAbbreviation: source?.abbreviation,
          leadsourceName: source?.name || source?.abbreviation,
          dealClosedCount: matched.length,
          deals: matched,
        };
      })
      .filter((s) => s.dealClosedCount > 0)
      .sort((a, b) => b.dealClosedCount - a.dealClosedCount);
  }, [leadsource, closedDealsCount]);
  const totalLeadSourceCount = leadSourceData.reduce(
    (s, r) => s + r.dealClosedCount,
    0,
  );
  const maxLeadSourceCount = Math.max(
    1,
    ...leadSourceData.map((r) => r.dealClosedCount),
  );
  const LEAD_SOURCE_COLORS = [
    "bg-[#1E293B]",
    "bg-indigo-700",
    "bg-emerald-600",
    "bg-amber-700",
    "bg-slate-500",
  ];

  // ── PER-AGENT STATS (for Team Roster panel — only computed in teamView) ──
  const agentStats = useMemo(() => {
    if (!teamView) return [];
    const perAgentCommission = (d, agent) =>
      d.assignedteamlead === agent.id
        ? Number(d.teamleadCommissionamount || 0)
        : Number(d.agentCommissionamount || 0);

    return teamMembers
      .map((agent) => {
        // Single-credit rule: a deal counts for the agent who actually owns
        // it, or the team lead when it's an in-house referral (or no agent).
        const agentDeals = deals.filter((d) => dealCreditsAgent(d, agent.id));
        const yearDeals = agentDeals.filter(
          (d) =>
            d.closingdate &&
            new Date(d.closingdate).getFullYear() === selectedYear,
        );
        const closed = yearDeals.filter(
          (d) => d.dealStage?.toLowerCase() === "closed",
        );
        const pending = yearDeals.filter(
          (d) =>
            !["closed", "cancelled", "expired"].includes(
              d.dealStage?.toLowerCase(),
            ),
        );
        return {
          user: agent,
          volume: closed.reduce((s, d) => s + Number(d.offerPrice || 0), 0),
          dealsClosed: closed.length,
          dealsActive: pending.length,
          gci: closed.reduce((s, d) => s + perAgentCommission(d, agent), 0),
          pendingVolume: pending.reduce(
            (s, d) => s + Number(d.offerPrice || 0),
            0,
          ),
          pendingComm: pending.reduce(
            (s, d) => s + perAgentCommission(d, agent),
            0,
          ),
        };
      })
      .sort((a, b) => b.volume - a.volume);
  }, [teamView, teamMembers, deals, selectedYear]);

  // ── HERO DERIVED ──
  const avgClosedComm =
    closedDealsCount.length > 0
      ? closedCommissionTotal / closedDealsCount.length
      : 0;

  if (!teamView && !selectedUser) return <div></div>;

  const initials = teamView ? "TE" : getInitials(selectedUser);
  const avatarBg = teamView ? "bg-[#1E293B]" : getAvatarClass(selectedUser);
  const roleLabel = teamView
    ? "Team"
    : selectedUser.role === "Admin"
      ? "Team Lead"
      : selectedUser.role === "Employee"
        ? "Agent"
        : selectedUser.role || "Member";
  const displayName = teamView
    ? "Team Edelstein"
    : `${selectedUser.firstname} ${selectedUser.lastname}`;
  const displayMeta = teamView
    ? `${teamMembers.length} agents · Coldwell Banker`
    : selectedUser.email || "Team Edelstein";
  const firstNameSafe = teamView
    ? "Team"
    : selectedUser.firstname || "Agent";
  const isEmployeeView = !teamView && selectedUser?.role === "Employee";

  // Goal Pacing panel — defined here so the same JSX renders in both the slim
  // dashboard-embedded view AND the full team-detail view. Reads closure
  // variables (volumeGoal, ytdVolume, projectedPct, etc.) from this scope.
  const goalPacingPanel = (
    <PanelShell
      icon={<Target size={14} strokeWidth={2} />}
      title="Goal Pacing"
      subtitle={
        volumeGoal > 0
          ? `Tracking against $${formatNumberShort(volumeGoal)} annual volume target`
          : "No annual volume goal set for this user"
      }
      right={
        volumeGoal > 0 && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11.5px] font-semibold ${
              isOnPace
                ? "bg-green-100 text-green-800"
                : "bg-amber-100 text-amber-800"
            }`}
          >
            {isOnPace ? (
              <CheckCircle size={11} strokeWidth={2.5} />
            ) : (
              <AlertTriangle size={11} strokeWidth={2.5} />
            )}
            {isOnPace ? "On Pace" : "Behind"}
          </span>
        )
      }
    >
      {volumeGoal > 0 ? (
        <>
          <div className="text-[32px] font-bold text-slate-900 -tracking-[0.025em] leading-none flex items-baseline gap-2">
            <span className="text-gray-400 font-medium">$</span>
            {formatNumberShort(displayVolume)}
            <span className="text-[13px] text-gray-500 font-medium">
              of ${formatNumberShort(volumeGoal)} goal
            </span>
          </div>
          <div className="my-2 mb-[18px] text-[12.5px] text-gray-500">
            Projected to finish at{" "}
            <strong className="text-slate-900 font-semibold">
              ${formatNumberShort(projectedYearEnd)}
            </strong>{" "}
            · {projectedPct.toFixed(1)}% of goal at current run rate
          </div>

          {/* Pacing bar — green segment = closed volume (where you are),
              yellow segment = pending volume stacked on top (only shown
              when "Include pending" toggle is on). */}
          <div className="relative h-8 mb-3">
            <div className="absolute top-3 left-0 right-0 h-2 bg-slate-100 rounded overflow-hidden flex">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${Math.min(volumePacePct, 100)}%` }}
              />
              {includePending && (
                <div
                  className="h-full bg-amber-400"
                  title={`Pending: $${formatNumberShort(ytdPendingVolume)}`}
                  style={{
                    width: `${Math.min(
                      volumeGoal > 0
                        ? (ytdPendingVolume / volumeGoal) * 100
                        : 0,
                      Math.max(0, 100 - Math.min(volumePacePct, 100)),
                    )}%`,
                  }}
                />
              )}
            </div>
            <div
              className="absolute top-1.5 w-0.5 h-5 bg-green-600"
              style={{ left: `${Math.min(expectedPacePct, 100)}%` }}
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white px-1.5 py-px rounded text-[9.5px] font-bold uppercase tracking-wider whitespace-nowrap">
                On pace
              </div>
            </div>
            <div
              className="absolute -bottom-4 -translate-x-1/2 text-[10.5px] font-semibold text-slate-900"
              style={{ left: `${Math.min(volumePacePct, 100)}%` }}
            >
              ${formatNumberShort(ytdVolume)} · {volumePacePct.toFixed(0)}%
            </div>
            <div className="absolute right-0 -bottom-4 text-[10.5px] font-medium text-gray-500">
              ${formatNumberShort(volumeGoal)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-[18px] border border-black/[0.04] rounded-lg overflow-hidden">
            <div className="p-[14px_16px] bg-gray-50 md:border-r border-black/[0.04]">
              <div className="text-xs text-gray-500 font-medium">
                Need to Hit Goal
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 flex items-baseline">
                <span className="text-gray-400 font-medium">$</span>
                {formatNumberShort(needPerMonth)}
                <span className="text-gray-400 text-[13px] font-medium ml-1">
                  / mo
                </span>
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1.5">
                Across remaining {monthsRemaining.toFixed(0)} months
              </div>
            </div>
            <div className="p-[14px_16px] bg-gray-50 md:border-r border-black/[0.04]">
              <div className="text-xs text-gray-500 font-medium">
                Current Run Rate
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 flex items-baseline">
                <span className="text-gray-400 font-medium">$</span>
                {formatNumberShort(runRatePerMonth)}
                <span className="text-gray-400 text-[13px] font-medium ml-1">
                  / mo
                </span>
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1.5">
                YTD average across {monthsElapsed.toFixed(1)} months
              </div>
            </div>
            <div className="p-[14px_16px] bg-gray-50">
              <div className="text-xs text-gray-500 font-medium">
                Days Remaining
              </div>
              <div className="text-[18px] font-semibold text-slate-900 mt-1 flex items-baseline">
                {daysRemaining}
                <span className="text-gray-400 text-[13px] font-medium ml-1">
                  days
                </span>
              </div>
              <div className="text-[11.5px] text-gray-500 mt-1.5">
                Until end of {selectedYear}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-[13px] text-gray-500 py-3">
          Set an annual volume goal for {firstNameSafe} to track pacing
          against target.
        </div>
      )}
    </PanelShell>
  );

  // ───── DASHBOARD-EMBEDDED VARIANT ─────
  // Slim version for the home dashboard hero. No page header / identity card —
  // those are already shown by the dashboard. Goal Pacing + Upcoming Closings
  // and (optionally) the monthly breakdown table.
  if (dashboardview) {
    return (
      <div className="space-y-4">
        {/* Goal Pacing intentionally omitted here — the navy DashboardStat
           hero on the dashboard already covers it. Goal Pacing still shows
           in the full team-detail view below. */}
        {user?.role !== "Accountant" && (
          <UpcomingClosingsPanel
            title="Upcoming Closings"
            deals={upcomingDeals}
            totalCommission={upcomingCommission}
            today={today}
            commissionForDeal={commissionFor}
          />
        )}
        {(selectedUser.role === "Admin" ||
          selectedUser.role === "Accountant") && (
          <UpcomingClosingsPanel
            title="Upcoming Team Closings"
            deals={upcomingTeamDeals}
            totalCommission={upcomingTeamCommission}
            today={today}
            commissionForDeal={(d) =>
              Number(d.takehomecommissionamount || 0)
            }
          />
        )}
        {isShowMonthly && (
          <MonthlyTablePanel
            monthlyData={monthlyData}
            currentMonth={currentMonth}
            selectedUser={selectedUser}
            gciGoal={gciGoal}
            PendingDealsCount={PendingDealsCount}
            closedDealsCount={closedDealsCount}
            ytdPendingVolume={ytdPendingVolume}
            ytdVolume={ytdVolume}
            closedCommissionTotal={closedCommissionTotal}
            pendingCommissionTotal={pendingCommissionTotal}
            TotalPct={TotalPct}
            filterYear={filterYear}
            yearDropdownVisible={yearDropdownVisible}
            setYearDropdownVisible={setYearDropdownVisible}
            setFilterYear={setFilterYear}
            setSelectedYear={setSelectedYear}
            setstatspopup={setstatspopup}
            setselectedstat={setselectedstat}
            setstatsdata={setstatsdata}
            dashboardview={true}
          />
        )}
        {statspopup && (
          <StatDetails
            Data={statsdata}
            selectedstat={selectedstat}
            onClose={() => setstatspopup(false)}
            isStatCard={true}
            statUserId={id}
            role={selectedUser?.role}
            onSubmit={(data) => alert(data)}
          />
        )}
      </div>
    );
  }

  // ───── FULL PAGE VARIANT ─────
  // When embedded on the dashboard (hideHeader=true), use a light top
  // padding — enough to breathe under the PageHeader, but not the full
  // pt-7 the standalone team-detail page uses.
  const topPad = hideHeader ? "pt-4" : "pt-7";
  const container = isWidth
    ? `max-w-[1380px] mx-auto px-9 ${topPad} pb-20`
    : `${topPad} pb-20`;

  return (
    <div className={hideHeader ? "" : "bg-slate-100 min-h-screen"}>
      {/* ============ NAVY PAGE HEADER ============ */}
      {!hideHeader && (
      <div className="bg-[#1E293B] text-white px-9 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {teamView ? "Team Performance" : `${displayName} — Performance`}
          </h1>
          <div className="text-xs text-white/55 mt-1 font-medium">
            <Link
              to="/dashboard"
              className="text-white/55 hover:text-white no-underline"
            >
              Dashboard
            </Link>
            <span className="opacity-40"> · </span>
            {teamView ? (
              <span className="text-white font-semibold">Team Performance</span>
            ) : (
              <>
                <Link
                  to="/dashboard/team-management"
                  className="text-white/55 hover:text-white no-underline"
                >
                  Team Performance
                </Link>
                <span className="opacity-40"> · </span>
                <span className="text-white font-semibold">{displayName}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!teamView && (
            <Link
              to="/dashboard/team-management"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/15 text-white px-3 py-1.5 rounded-md text-[13px] font-medium no-underline"
            >
              <ChevronLeft size={14} />
              Back to Team
            </Link>
          )}
          <AgentPicker
            visible={agentDropdownVisible}
            setVisible={setAgentDropdownVisible}
            teamView={teamView}
            currentId={selectedUser?.id}
            displayName={displayName}
            teamMembers={teamMembers}
            onSelect={(target) => {
              setAgentDropdownVisible(false);
              if (target === "all") {
                navigate("/dashboard/team-management");
              } else {
                navigate(`/dashboard/team-management/${target}`);
              }
            }}
          />
          <div className="relative">
            <button
              onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
              className="bg-white/10 hover:bg-white/15 border border-white/15 text-white px-3 py-1.5 rounded-md text-[13px] font-medium cursor-pointer"
            >
              {filterYear} · YTD
            </button>
            <YearFilterDropdown
              visible={yearDropdownVisible}
              onSelect={(year) => {
                setFilterYear(year);
                setSelectedYear(Number(year));
                setYearDropdownVisible(false);
              }}
              activateOutsideClick={() => setYearDropdownVisible(false)}
              style={{ top: "40px", right: "0px" }}
            />
          </div>
        </div>
      </div>
      )}

      <div className={container}>
        {/* ============ IDENTITY CARD ============ */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[0.04] px-7 py-6 mb-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`w-14 h-14 rounded-full ${avatarBg} text-white flex items-center justify-center font-semibold text-[17px] tracking-wide flex-shrink-0 overflow-hidden`}
            >
              {!teamView && selectedUser?.profilepicture ? (
                <img
                  src={selectedUser.profilepicture}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                initials
              )}
            </div>
            <div>
              <div className="text-[22px] font-semibold text-slate-900 tracking-tight">
                {displayName}
              </div>
              <div className="mt-1 text-[13px] text-gray-500 flex items-center gap-2">
                <span
                  className={`${
                    teamView || selectedUser?.role === "Admin"
                      ? "bg-[#1E293B]"
                      : "bg-gray-500"
                  } text-white px-2 py-[3px] text-[10.5px] font-semibold rounded uppercase tracking-wider`}
                >
                  {roleLabel}
                </span>
                <span className="w-[3px] h-[3px] rounded-full bg-slate-300"></span>
                <span>{displayMeta}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <span
              className={`inline-flex items-center rounded-full px-4 py-1.5 text-[13px] font-medium ${
                volumeGoal > 0
                  ? isOnPace
                    ? "bg-green-100 text-green-800"
                    : "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-700"
              }`}
            >
              {volumeGoal > 0
                ? `${isOnPace ? "On Pace" : "Behind Pace"} · ${projectedPct.toFixed(1)}% projected`
                : "No goal set"}
            </span>
            {volumeGoal > 0 && (
              <button
                type="button"
                onClick={() => setIncludePending((v) => !v)}
                title={
                  includePending
                    ? "Showing closed + pending volume. Click to switch to closed only."
                    : "Showing closed volume only. Click to include pending."
                }
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-800"
              >
                <span
                  className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                    includePending ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      includePending ? "translate-x-3.5" : "translate-x-0.5"
                    }`}
                  />
                </span>
                <span>Include pending</span>
              </button>
            )}
          </div>
        </div>

        {/* ============ HERO STATS ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-0 border border-black/[0.04] rounded-xl bg-white overflow-hidden shadow-sm mb-5">
          <div className="p-[22px_26px] lg:border-r border-black/[0.06]">
            <div className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold mb-2.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-600 shadow-[0_0_0_3px_rgba(22,163,74,0.15)]"></span>
              Year to date · {selectedYear}
            </div>
            <div className="text-4xl font-bold text-slate-900 -tracking-[0.02em] leading-none flex items-baseline gap-2.5">
              <div>
                <span className="text-gray-400 font-medium text-[0.65em]">
                  $
                </span>
                {formatNumberShort(displayVolume)}
              </div>
              {yoyVolumePct !== null && (
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11.5px] font-semibold ${
                    yoyVolumePct >= 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {yoyVolumePct >= 0 ? (
                    <ArrowUp size={11} strokeWidth={2.5} />
                  ) : (
                    <TrendingDown size={11} strokeWidth={2.5} />
                  )}
                  {Math.abs(yoyVolumePct).toFixed(1)}% YoY
                </span>
              )}
            </div>
            <div className="mt-2 text-[12.5px] text-gray-500">
              {includePending
                ? `Closed + pending volume across ${displayUnits} deals`
                : `Closed transaction volume across ${closedDealsCount.length} deals`}
            </div>
            {volumeGoal > 0 && (
              <div className="mt-2.5">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700">
                  {Math.round(volumePacePct)}% of goal
                </span>
              </div>
            )}
          </div>

          <div className="p-[22px_26px] lg:border-r border-black/[0.06]">
            <div className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold mb-2.5">
              Closed Commission
            </div>
            <div className="text-[26px] font-bold text-slate-900 -tracking-[0.015em] leading-none flex items-baseline gap-2">
              <div>
                <span className="text-gray-400 font-medium">$</span>
                {formatNumberShort(closedCommissionTotal)}
              </div>
              {closedGciYoy !== null && (
                <span
                  className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${
                    closedGciYoy >= 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {closedGciYoy >= 0 ? "↑" : "↓"}
                  {Math.abs(closedGciYoy).toFixed(1)}% YoY
                </span>
              )}
            </div>
            <div className="mt-1.5 text-[11.5px] text-gray-500">
              ${formatNumberShort(avgClosedComm)} avg per deal
            </div>
            {closedGciPacePct !== null && (
              <div className="mt-2.5">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700">
                  {Math.round(closedGciPacePct)}% of goal
                </span>
              </div>
            )}
          </div>

          <div className="p-[22px_26px]">
            <div className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold mb-2.5">
              Pending Commission
            </div>
            <div className="text-[26px] font-bold text-amber-700 -tracking-[0.015em] leading-none">
              <span className="text-amber-600 font-medium">$</span>
              {formatNumberShort(pendingCommissionTotal)}
            </div>
            <div className="mt-1.5 text-[11.5px] text-gray-500">
              {pendingDealsYTD.length} deals expected to close
            </div>
          </div>
        </div>

        {/* ============ KPI MINI-CARDS ============
            Through {today.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            · {selectedYear} vs {priorYear}. The YoY % and "of goal" pills
            on the tiles below absorbed what used to be a separate "Year
            over Year vs Goal" row so the same numbers don't appear twice. */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <MiniCard
            label="YTD Pending Volume"
            value={`$${formatNumberShort(ytdPendingVolume)}`}
            sub={`${PendingDealsCount.length} deals`}
            onClick={() => {
              setstatspopup(true);
              setselectedstat("Deals - Pending Volume");
              setstatsdata(PendingDealsCount);
            }}
          />
          <MiniCard
            label="Net YTD Volume"
            value={`$${formatNumberShort(ytdnetVolume)}`}
            sub="Closed + pending"
            onClick={() => {
              setstatspopup(true);
              setselectedstat("Deals - Net Volume");
              setstatsdata(AllDealsCount);
            }}
          />
          <MiniCard
            label={includePending ? "Deals" : "Closed Deals"}
            value={`${displayUnits}`}
            yoyPct={unitsYoy}
            pacePct={unitsPacePct}
            onClick={() => {
              setstatspopup(true);
              setselectedstat(
                includePending ? "Deals - Closed + Pending" : "Deals - Closed",
              );
              setstatsdata(
                includePending
                  ? [...closedDealsCount, ...PendingDealsCount]
                  : closedDealsCount,
              );
            }}
          />
          <MiniCard
            label="Net Commission"
            value={`$${formatNumberShort(netCommissionTotal)}`}
            sub="Closed + pending"
            onClick={() => {
              setstatspopup(true);
              setselectedstat("Deals - Net Commission");
              setstatsdata(netCommissionDealsYTD);
            }}
          />
          <MiniCard
            label="Avg Sale Price"
            value={`$${formatNumberShort(displayAvg)}`}
            yoyPct={avgYoy}
          />
        </div>

        {/* ============ GOAL PACING ============ */}
        {goalPacingPanel}

        {/* ============ ACTIVE LISTINGS ============ */}
        <PanelShell
          icon={<Home size={14} strokeWidth={2} />}
          title="Active Listings"
          subtitle={
            activeListings.length === 0 &&
            comingSoonListings.length === 0 &&
            preMarketListings.length === 0
              ? "No active, coming-soon, or pre-market listings"
              : `${activeListings.length} active · ${comingSoonListings.length} coming soon · ${preMarketListings.length} pre-market`
          }
        >
          {activeListings.length === 0 &&
          comingSoonListings.length === 0 &&
          preMarketListings.length === 0 ? (
            <div className="text-[12.5px] text-gray-500 py-2">
              Listings will appear here when their stage moves to Active,
              Coming Soon, or Pre-Market.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {[
                ...activeListings.map((d) => ({ ...d, _stageLabel: "Active" })),
                ...comingSoonListings.map((d) => ({
                  ...d,
                  _stageLabel: "Coming Soon",
                })),
                ...preMarketListings.map((d) => ({
                  ...d,
                  _stageLabel: "Pre-Market",
                })),
              ]
                .sort((a, b) =>
                  (a.propertyaddress || "").localeCompare(
                    b.propertyaddress || "",
                  ),
                )
                .map((d) => {
                  const price = Number(
                    d.listPrice || d.suggestedPrice || d.offerPrice || 0,
                  );
                  const stageStyle =
                    d._stageLabel === "Active"
                      ? "bg-emerald-100 text-emerald-700"
                      : d._stageLabel === "Coming Soon"
                        ? "bg-slate-100 text-slate-700"
                        : "bg-indigo-100 text-indigo-700";
                  return (
                    <li
                      key={d.createdat || d.id}
                      onClick={() =>
                        navigate(`/dashboard/deals-detail/${d.createdat}`)
                      }
                      className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-100 hover:border-slate-300 hover:bg-slate-50 cursor-pointer transition"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {d.propertyaddress || "Untitled listing"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                          <span
                            className={`px-1.5 py-px rounded text-[10px] font-semibold uppercase tracking-wider ${stageStyle}`}
                          >
                            {d._stageLabel}
                          </span>
                          {d.closingdate && (
                            <span>Closing {formatDate(d.closingdate)}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold text-slate-900">
                          {price > 0
                            ? `$${price.toLocaleString("en-US")}`
                            : "—"}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          )}
        </PanelShell>

        {/* ============ TEAM ROSTER (team view only) ============ */}
        {teamView && (
          <PanelShell
            icon={<Users size={14} strokeWidth={2} />}
            title="Team Roster"
            subtitle="Click an agent to drill into their personal view"
          >
            <div className="grid grid-cols-[28px_1.4fr_0.9fr_0.55fr_0.85fr_0.7fr_24px] gap-3 items-center pb-3 border-b border-gray-200">
              <span></span>
              <span className="pl-1 text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Agent
              </span>
              <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Closed Volume
              </span>
              <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Deals
              </span>
              <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                Pending
              </span>
              <span className="text-[10.5px] text-gray-500 uppercase tracking-wider font-semibold">
                GCI
              </span>
              <span></span>
            </div>
            {agentStats.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-[13px]">
                No team members found
              </div>
            ) : (
              agentStats.map((row, i) => {
                const agentInitials = getInitials(row.user);
                const agentAvatar = getAvatarClass(row.user);
                const subRole =
                  row.user.role === "Admin"
                    ? "Team Lead"
                    : row.user.role === "Employee"
                      ? "Agent"
                      : row.user.role || "Member";
                return (
                  <Link
                    key={row.user.id}
                    to={`/dashboard/team-management/${row.user.id}`}
                    className="no-underline"
                  >
                    <div className="grid grid-cols-[28px_1.4fr_0.9fr_0.55fr_0.85fr_0.7fr_24px] gap-3 items-center py-3.5 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-gray-50 group transition">
                      <div
                        className={`text-[13px] font-semibold text-center ${
                          i < 3 ? "text-[#1E293B]" : "text-gray-400"
                        }`}
                      >
                        {i + 1}
                      </div>
                      <div className="flex items-center gap-3 min-w-0 pl-1">
                        <div
                          className={`w-9 h-9 rounded-full ${agentAvatar} text-white flex items-center justify-center font-semibold text-[12.5px] tracking-wide flex-shrink-0 overflow-hidden`}
                        >
                          {row.user?.profilepicture ? (
                            <img
                              src={row.user.profilepicture}
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            agentInitials
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 leading-tight truncate">
                            {row.user.firstname} {row.user.lastname}
                          </div>
                          <div className="text-[11.5px] text-gray-500 mt-0.5 truncate">
                            {subRole}
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          <span className="text-gray-400 font-medium">$</span>
                          {formatNumberShort(row.volume)}
                        </div>
                        <div className="text-[11.5px] text-gray-500 mt-0.5 font-medium">
                          ${formatNumberShort(row.gci)} GCI
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          {row.dealsClosed}
                        </div>
                        <div className="text-[11.5px] text-gray-500 mt-0.5 font-medium">
                          {row.dealsActive} active
                        </div>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">
                          <span className="text-gray-400 font-medium">$</span>
                          {formatNumberShort(row.pendingVolume)}
                        </div>
                        <div className="text-[11.5px] text-gray-500 mt-0.5 font-medium">
                          ${formatNumberShort(row.pendingComm)} pending
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-slate-900">
                        <span className="text-gray-400 font-medium">$</span>
                        {formatNumberShort(row.gci)}
                      </div>
                      <ChevronRight
                        size={16}
                        className="text-slate-300 group-hover:text-[#1E293B] group-hover:translate-x-0.5 transition"
                      />
                    </div>
                  </Link>
                );
              })
            )}
          </PanelShell>
        )}

        {/* ============ PIPELINE DISTRIBUTION ============ */}
        <PanelShell
          icon={<Filter size={14} strokeWidth={2} />}
          title="Pipeline Distribution"
          subtitle={`${openPipelineCount} open deals across the pipeline`}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <MiniCard
              label="Open Pipeline"
              value={`$${formatNumberShort(openPipelineVolume)}`}
            />
            <MiniCard
              label="Deals in Flight"
              value={`${openPipelineCount}`}
            />
            <MiniCard
              label="Expected GCI"
              value={`$${formatNumberShort(expectedOpenGci)}`}
            />
            <MiniCard
              label="Stages With Activity"
              value={`${pipelineRows.filter((r) => r.count > 0).length}`}
            />
          </div>

          <div className="flex items-center gap-3 mt-[22px] mb-3.5">
            <span className="text-[11px] font-semibold text-[#1E293B] tracking-wider uppercase">
              Stage Breakdown
            </span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {pipelineRows.map((s) => {
            const widthPct =
              maxPipelineCount > 0
                ? Math.max(2, (s.count / maxPipelineCount) * 100)
                : 0;
            return (
              <div
                key={s.key}
                className="grid grid-cols-[130px_1fr_90px_70px] items-center gap-3.5 py-2"
              >
                <div className="flex items-center gap-2.5 text-[13px] font-medium text-slate-900">
                  <span
                    className="w-2 h-2 rounded-sm flex-shrink-0"
                    style={{ background: s.color }}
                  ></span>
                  {s.label}
                </div>
                <div className="h-6 bg-slate-100 rounded-md overflow-hidden relative">
                  {s.count > 0 && (
                    <div
                      className="h-full rounded-md flex items-center pl-2.5 text-white text-[11px] font-semibold transition-all duration-500"
                      style={{
                        width: `${widthPct}%`,
                        background: s.color,
                      }}
                    >
                      {s.count} {s.count === 1 ? "deal" : "deals"}
                    </div>
                  )}
                </div>
                <div className="text-right text-[13px] font-semibold text-slate-900">
                  <span className="text-gray-400 font-medium">$</span>
                  {formatNumberShort(s.volume)}
                </div>
                <div className="text-right text-xs text-gray-500 font-medium">
                  <strong className="text-slate-900 font-semibold mr-0.5">
                    {s.count}
                  </strong>
                  deals
                </div>
              </div>
            );
          })}
        </PanelShell>

        {/* ============ CYCLE TIME + CONVERSION (shells) ============ */}
        {/* Hidden for Employees — these team-wide analytics aren't useful
            to a single agent and clutter their dashboard. Visible to
            Admin / Supervisor / Accountant. */}
        {user?.role !== "Employee" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <PanelShell
            icon={<Clock size={14} strokeWidth={2} />}
            title="Cycle Time Analysis"
            subtitle={`Median time across deal lifecycle · ${selectedYear}`}
          >
            <div className="grid grid-cols-2 gap-0">
              <CycleCell
                label="Lead → Appointment"
                value={
                  cycleLeadToAppt.median != null
                    ? Math.round(cycleLeadToAppt.median)
                    : "—"
                }
                foot={
                  cycleLeadToAppt.sample > 0
                    ? `From ${cycleLeadToAppt.sample} appointment${cycleLeadToAppt.sample === 1 ? "" : "s"}`
                    : "No appointments logged"
                }
              />
              <CycleCell
                label="Listing → Under Contract"
                value={
                  cycleListToUc.median != null
                    ? Math.round(cycleListToUc.median)
                    : "—"
                }
                foot={
                  cycleListToUc.sample > 0
                    ? `From ${cycleListToUc.sample} deal${cycleListToUc.sample === 1 ? "" : "s"}`
                    : "Stamped on future stage flips"
                }
              />
              <CycleCell
                label="Contract → Close"
                value={
                  cycleUcToClose.median != null
                    ? Math.round(cycleUcToClose.median)
                    : "—"
                }
                foot={
                  cycleUcToClose.sample > 0
                    ? `From ${cycleUcToClose.sample} closed deal${cycleUcToClose.sample === 1 ? "" : "s"}`
                    : "Stamped on future stage flips"
                }
              />
              <CycleCell
                label="Lead → Close (Full)"
                value={
                  cycleLeadToClose.median != null
                    ? Math.round(cycleLeadToClose.median)
                    : "—"
                }
                foot={
                  cycleLeadToClose.sample > 0
                    ? `From ${cycleLeadToClose.sample} closed deal${cycleLeadToClose.sample === 1 ? "" : "s"}`
                    : "No closed deals yet"
                }
              />
            </div>
          </PanelShell>

          <PanelShell
            icon={<Filter size={14} strokeWidth={2} />}
            title="Conversion Funnel"
            subtitle={`Seller-side pull-through · ${selectedYear}`}
          >
            <div className="flex items-center justify-between gap-1 py-1">
              <ConvStage num={funnel.cma} label="CMA's" />
              <ConvArrow />
              <ConvStage num={funnel.listed} label="Listed" />
              <ConvArrow />
              <ConvStage num={funnel.uc} label="Under Contract" />
              <ConvArrow />
              <ConvStage num={funnel.closed} label="Closed" emphasize />
            </div>
            <div className="grid grid-cols-4 gap-1 mt-4 pt-4 border-t border-gray-200">
              <ConvRate pct={funnel.cmaToList} label="CMA → List" />
              <ConvRate pct={funnel.listToUc} label="List → UC" />
              <ConvRate pct={funnel.ucToClosed} label="UC → Closed" />
              <ConvRate pct={funnel.overall} label="Overall" />
            </div>
          </PanelShell>
        </div>
        )}

        {/* ============ MONTHLY CHART + UPCOMING CLOSINGS ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 mb-5">
          <PanelShell
            icon={<BarChart3 size={14} strokeWidth={2} />}
            title={`${teamView ? "Team" : firstNameSafe}${teamView ? "" : "'s"} Monthly Performance`}
            subtitle={`Net commission · ${selectedYear} vs ${priorYear}`}
          >
            <MonthlyChart
              monthlyData={monthlyData}
              priorYearMonthly={priorYearMonthly}
              currentMonth={currentMonth}
              selectedYear={selectedYear}
            />
          </PanelShell>

          <UpcomingClosingsPanel
            title="Upcoming Closings"
            deals={upcomingDeals}
            totalCommission={upcomingCommission}
            today={today}
            commissionForDeal={commissionFor}
            tight
          />
        </div>

        {/* ============ LEAD SOURCES ============ */}
        {leadSourceData.length > 0 && (
          <PanelShell
            icon={<Activity size={14} strokeWidth={2} />}
            title="Lead Sources · Closed Transactions"
            subtitle={`YTD ${selectedYear} · ${totalLeadSourceCount} closings`}
          >
            <div className="grid grid-cols-[110px_1fr_80px_70px] gap-3.5 pb-2.5 border-b border-gray-200 mb-1">
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                Source
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                Distribution
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold text-right">
                Share
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold text-right">
                Closings
              </span>
            </div>
            {leadSourceData.map((s, i) => {
              const pct =
                totalLeadSourceCount > 0
                  ? Math.round((s.dealClosedCount / totalLeadSourceCount) * 100)
                  : 0;
              const w = (s.dealClosedCount / maxLeadSourceCount) * 100;
              const colorCls =
                LEAD_SOURCE_COLORS[i % LEAD_SOURCE_COLORS.length];
              return (
                <div
                  key={s.leadsourceId}
                  className="grid grid-cols-[110px_1fr_80px_70px] items-center gap-3.5 py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setstatspopup(true);
                    setselectedstat("Deals - Lead Source");
                    setstatsdata(s.deals);
                  }}
                >
                  <div className="text-[13px] font-semibold text-slate-900">
                    {s.leadsourceName}
                    <span className="block text-[11px] font-medium text-gray-500 mt-0.5">
                      {s.leadsourceAbbreviation}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                    <div
                      className={`h-full rounded-sm transition-all duration-500 ${colorCls}`}
                      style={{ width: `${w}%` }}
                    ></div>
                  </div>
                  <div className="text-right text-xs text-slate-600 font-medium">
                    {pct}%
                  </div>
                  <div className="text-right text-sm font-semibold text-slate-900">
                    {s.dealClosedCount}
                  </div>
                </div>
              );
            })}
          </PanelShell>
        )}

        {/* ============ MONTHLY BREAKDOWN TABLE ============ */}
        {isShowMonthly && (
          <MonthlyTablePanel
            monthlyData={monthlyData}
            currentMonth={currentMonth}
            selectedUser={selectedUser}
            gciGoal={gciGoal}
            PendingDealsCount={PendingDealsCount}
            closedDealsCount={closedDealsCount}
            ytdPendingVolume={ytdPendingVolume}
            ytdVolume={ytdVolume}
            closedCommissionTotal={closedCommissionTotal}
            pendingCommissionTotal={pendingCommissionTotal}
            TotalPct={TotalPct}
            filterYear={filterYear}
            yearDropdownVisible={yearDropdownVisible}
            setYearDropdownVisible={setYearDropdownVisible}
            setFilterYear={setFilterYear}
            setSelectedYear={setSelectedYear}
            setstatspopup={setstatspopup}
            setselectedstat={setselectedstat}
            setstatsdata={setstatsdata}
            dashboardview={false}
          />
        )}
      </div>

      {statspopup && (
        <StatDetails
          Data={statsdata}
          selectedstat={selectedstat}
          onClose={() => setstatspopup(false)}
          isStatCard={true}
          statUserId={id}
          role={selectedUser?.role}
          onSubmit={(data) => alert(data)}
        />
      )}
    </div>
  );
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

function AgentPicker({
  visible,
  setVisible,
  teamView,
  currentId,
  displayName,
  teamMembers,
  onSelect,
}) {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setVisible(false);
      }
    };
    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [visible, setVisible]);

  const label = teamView ? "All Team" : displayName || "Select Agent";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setVisible(!visible)}
        className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 border border-white/15 text-white px-3 py-1.5 rounded-md text-[13px] font-medium cursor-pointer"
      >
        <Users size={13} />
        {label}
      </button>
      {visible && (
        <div
          className="absolute right-0 top-10 z-20 w-[240px] bg-white border border-gray-300 rounded-md p-2 shadow-lg text-gray-900"
        >
          <div className="max-h-[300px] overflow-y-auto">
            <div
              onClick={() => onSelect("all")}
              className={`py-1.5 px-3 cursor-pointer rounded-md text-[13px] font-semibold ${
                teamView
                  ? "bg-slate-100 text-slate-900"
                  : "text-gray-800 hover:bg-gray-100"
              }`}
            >
              All Team
            </div>
            <div className="border-t border-gray-100 my-1" />
            {teamMembers.map((u) => {
              const isCurrent = !teamView && u.id === currentId;
              return (
                <div
                  key={u.id}
                  onClick={() => onSelect(u.id)}
                  className={`py-1.5 px-3 cursor-pointer rounded-md text-[13px] ${
                    isCurrent
                      ? "bg-slate-100 text-slate-900 font-semibold"
                      : "text-gray-800 hover:bg-gray-100"
                  }`}
                >
                  {u.firstname} {u.lastname}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function YoyVsGoalCard({ label, ytdValue, yoyPct, pacePct }) {
  return (
    <div className="bg-white rounded-xl border border-black/[0.06] shadow-sm p-4">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-gray-500">
        {label}
      </div>
      <div className="mt-1.5 text-[22px] font-bold text-slate-900 -tracking-[0.01em] leading-none">
        {ytdValue}
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        <YoyBadge pct={yoyPct} />
        <PaceBadge pct={pacePct} />
      </div>
    </div>
  );
}

function YoyBadge({ pct }) {
  if (pct === null) {
    return (
      <span className="text-[11px] text-gray-400">No prior-year data</span>
    );
  }
  const isUp = pct >= 0;
  const cls = isUp
    ? "text-green-700 bg-green-50"
    : "text-red-700 bg-red-50";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-0.5 rounded ${cls} w-fit`}
    >
      {isUp ? (
        <ArrowUp size={11} strokeWidth={2.5} />
      ) : (
        <TrendingDown size={11} strokeWidth={2.5} />
      )}
      {Math.abs(pct).toFixed(1)}% vs last yr same date
    </span>
  );
}

function PaceBadge({ pct }) {
  if (pct === null) {
    return (
      <span className="text-[11px] text-gray-400">No goal set</span>
    );
  }
  return (
    <span className="inline-flex items-center text-[11.5px] font-semibold px-2 py-0.5 rounded text-slate-700 bg-slate-100 w-fit">
      {pct.toFixed(0)}% of goal
    </span>
  );
}

function PanelShell({
  icon,
  iconBg = "bg-[#1E293B]",
  title,
  subtitle,
  right,
  tight,
  children,
}) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5 ${
        tight ? "p-0" : "p-6"
      }`}
    >
      <div
        className={`flex items-center justify-between border-b border-black/[0.08] ${
          tight ? "px-6 pt-5 pb-4" : "pb-3.5 mb-[18px]"
        }`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`w-7 h-7 ${iconBg} rounded-md inline-flex items-center justify-center flex-shrink-0 text-white`}
          >
            {icon}
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              {title}
            </div>
            {subtitle && (
              <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>
            )}
          </div>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function MiniCard({ label, value, sub, onClick, yoyPct, pacePct }) {
  return (
    <div
      onClick={onClick}
      className={`p-[14px_16px] bg-gray-50 rounded-lg border border-black/[0.03] ${
        onClick ? "cursor-pointer hover:bg-slate-100 hover:border-black/[0.08] transition" : ""
      }`}
    >
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className="text-[18px] font-semibold text-slate-900 mt-1 -tracking-[0.005em]">
        {value}
      </div>
      {(yoyPct !== undefined && yoyPct !== null) || pacePct !== undefined ? (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {yoyPct !== undefined && yoyPct !== null && (
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10.5px] font-semibold ${
                yoyPct >= 0
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {yoyPct >= 0 ? "↑" : "↓"}
              {Math.abs(yoyPct).toFixed(1)}% YoY
            </span>
          )}
          {pacePct !== undefined && pacePct !== null && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold bg-slate-100 text-slate-700">
              {Math.round(pacePct)}% of goal
            </span>
          )}
          {pacePct === undefined && yoyPct === undefined && null}
        </div>
      ) : null}
      {sub && <div className="text-[11.5px] text-gray-500 mt-1.5">{sub}</div>}
    </div>
  );
}

function CycleCell({ label, value = "—", unit = "days", trend, foot }) {
  return (
    <div className="p-[16px_18px] border-r border-slate-100 border-b border-slate-100 last:border-r-0 [&:nth-child(2n)]:border-r-0 [&:nth-child(n+3)]:border-b-0">
      <div className="text-xs text-gray-500 font-medium mb-2">{label}</div>
      <div>
        <span className="text-[26px] font-bold text-slate-900 -tracking-[0.02em] leading-none">
          {value}
        </span>
        <span className="text-xs text-gray-400 font-medium ml-1">{unit}</span>
        {trend && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 ml-2">
            {trend}
          </span>
        )}
      </div>
      {foot && (
        <div className="mt-1.5 text-[11px] text-gray-500">{foot}</div>
      )}
    </div>
  );
}

function ConvStage({ num, label, emphasize }) {
  return (
    <div className="flex-1 text-center">
      <div
        className={`text-[22px] font-bold -tracking-[0.02em] leading-none ${
          emphasize ? "text-green-700" : "text-slate-900"
        }`}
      >
        {num}
      </div>
      <div
        className={`mt-1.5 text-[11px] font-medium ${
          emphasize ? "text-green-700 font-semibold" : "text-gray-500"
        }`}
      >
        {label}
      </div>
    </div>
  );
}

function ConvArrow() {
  return (
    <span className="flex-shrink-0 text-slate-300 px-1">
      <ChevronRight size={14} />
    </span>
  );
}

function ConvRate({ pct, label }) {
  return (
    <div className="text-center">
      <div className="text-base font-bold text-slate-900 -tracking-[0.01em]">
        {pct}
      </div>
      <div className="text-[10px] text-gray-400 mt-1 tracking-wider uppercase font-semibold">
        {label}
      </div>
    </div>
  );
}

// Monthly chart with current-year bars + prior-year YoY dashed trend line.
function MonthlyChart({
  monthlyData,
  priorYearMonthly,
  currentMonth,
  selectedYear,
}) {
  // Compute scale.
  const maxCurrent = Math.max(...monthlyData.map((m) => m.netCommission), 0);
  const maxPrior = Math.max(...priorYearMonthly, 0);
  const maxValue = Math.max(maxCurrent, maxPrior, 1);
  const chartHeight = 160;
  const chartTop = 20;
  const chartBottom = 180;

  // Y-axis ticks (4 lines).
  const ticks = [maxValue, maxValue * 0.66, maxValue * 0.33, 0];

  // X positions: 12 bars between x=60 and x=676 (step ~55px).
  const xFor = (i) => 60 + i * 55;
  const yFor = (val) =>
    chartBottom - (val / maxValue) * (chartBottom - chartTop);

  return (
    <div className="pt-2 pb-1">
      <svg
        className="w-full h-[220px]"
        viewBox="0 0 720 220"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1E293B" />
            <stop offset="100%" stopColor="#475569" />
          </linearGradient>
          <linearGradient id="barGradLight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </linearGradient>
        </defs>

        {/* Grid lines + Y-axis labels */}
        {ticks.map((t, i) => {
          const y = chartTop + i * ((chartBottom - chartTop) / 3);
          return (
            <g key={i}>
              <line
                x1="40"
                y1={y}
                x2="710"
                y2={y}
                stroke={i === 3 ? "#cbd5e1" : "#f1f5f9"}
              />
              <text
                x="34"
                y={y + 4}
                fill="#9ca3af"
                fontSize="9.5"
                textAnchor="end"
              >
                ${formatNumberShort(t)}
              </text>
            </g>
          );
        })}

        {/* Prior-year trend line */}
        {maxPrior > 0 && (
          <polyline
            points={priorYearMonthly
              .map((v, i) => `${xFor(i) + 11},${yFor(v)}`)
              .join(" ")}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="1.5"
            strokeDasharray="4 3"
            strokeLinecap="round"
          />
        )}

        {/* Current-year bars */}
        {monthlyData.map((m, i) => {
          const x = xFor(i);
          const isFuture = i > currentMonth;
          const isNow = i === currentMonth;
          const closedH = (m.closedCommission / maxValue) * chartHeight;
          const pendingH = (m.pendingCommission / maxValue) * chartHeight;
          const closedY = chartBottom - closedH;
          const pendingY = closedY - pendingH;
          return (
            <g key={i}>
              {isFuture ? (
                <rect
                  x={x}
                  y={chartBottom - 6}
                  width="22"
                  height="6"
                  rx="3"
                  fill="#f1f5f9"
                />
              ) : (
                <>
                  {m.closedCommission > 0 && (
                    <rect
                      x={x}
                      y={closedY}
                      width="22"
                      height={Math.max(2, closedH)}
                      rx="3"
                      fill="url(#barGrad)"
                    />
                  )}
                  {m.pendingCommission > 0 && (
                    <rect
                      x={x}
                      y={pendingY}
                      width="22"
                      height={Math.max(2, pendingH)}
                      fill="#f59e0b"
                      opacity="0.85"
                    />
                  )}
                </>
              )}
              {isNow && m.netCommission > 0 && (
                <text
                  x={x + 11}
                  y={pendingY - 6}
                  fill="#1E293B"
                  fontSize="10.5"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  ${formatNumberShort(m.netCommission)}
                </text>
              )}
            </g>
          );
        })}

        {/* Month labels */}
        <g fill="#6b7280" fontSize="10.5" textAnchor="middle" fontWeight="500">
          {MONTH_NAMES_SHORT.map((mn, i) => {
            const isNow = i === currentMonth;
            const isFuture = i > currentMonth;
            return (
              <text
                key={i}
                x={xFor(i) + 11}
                y={isNow ? 208 : 200}
                fill={isNow ? "#1E293B" : isFuture ? "#cbd5e1" : "#6b7280"}
                fontWeight={isNow ? "700" : "500"}
              >
                {mn}
              </text>
            );
          })}
        </g>
      </svg>

      <div className="flex gap-4 items-center mt-3.5 pt-3 border-t border-black/[0.04]">
        <LegendItem label={`Closed ${selectedYear}`} colorClass="bg-[#1E293B]" />
        <LegendItem label="Pending" colorStyle="bg-amber-500 opacity-85" />
        <LegendItem
          label="Future"
          colorStyle="border border-dashed border-slate-300 bg-slate-200"
        />
        <LegendItem
          label={`${selectedYear - 1} YoY`}
          colorClass="!w-4 !h-[1.5px] bg-gray-400"
        />
      </div>
    </div>
  );
}

function LegendItem({ label, colorClass = "", colorStyle = "" }) {
  return (
    <div className="flex items-center gap-1.5 text-[11.5px] text-gray-500 font-medium">
      <span
        className={`w-2.5 h-2.5 rounded-sm ${colorClass} ${colorStyle}`}
      ></span>
      {label}
    </div>
  );
}

function UpcomingClosingsPanel({
  title,
  deals,
  totalCommission,
  today,
  commissionForDeal,
  tight,
}) {
  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5 overflow-hidden flex flex-col ${
        tight ? "" : ""
      }`}
    >
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#1E293B] rounded-md inline-flex items-center justify-center flex-shrink-0">
            <Calendar size={14} strokeWidth={2} className="text-white" />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              {title}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              {deals.length} deals · $
              {Math.round(totalCommission || 0).toLocaleString("en-US")}{" "}
              expected
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex-1 overflow-y-auto"
        style={{ maxHeight: "480px" }}
      >
        {deals.length > 0 ? (
          deals.map((deal) => {
            const daysLeft = Math.ceil(
              (new Date(deal.closingdate) - today) / 86400000,
            );
            const isUrgent = daysLeft <= 7;
            const isSoon = daysLeft <= 14;
            const clientName =
              deal.customers?.map((c) => c.clientName).join(" & ") || "—";
            return (
              <Link
                key={deal.id || deal.createdat}
                to={`/dashboard/deals-detail/${deal.createdat}`}
                className="no-underline"
              >
                <div className="px-5 py-3 flex items-center gap-3.5 border-b border-slate-100 last:border-b-0 hover:bg-gray-50/50 transition cursor-pointer">
                  <div
                    className={`w-11 h-11 rounded-lg flex flex-col items-center justify-center flex-shrink-0 border ${
                      isUrgent
                        ? "bg-red-50 border-red-200"
                        : isSoon
                          ? "bg-amber-50 border-amber-200"
                          : "bg-slate-100 border-slate-200"
                    }`}
                  >
                    <span
                      className={`text-base font-bold leading-none ${
                        isUrgent
                          ? "text-red-700"
                          : isSoon
                            ? "text-amber-700"
                            : "text-slate-900"
                      }`}
                    >
                      {daysLeft}
                    </span>
                    <span
                      className={`text-[9px] mt-0.5 font-semibold tracking-wider ${
                        isUrgent
                          ? "text-red-500"
                          : isSoon
                            ? "text-amber-600"
                            : "text-gray-400"
                      }`}
                    >
                      DAYS
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[13.5px] text-slate-900 truncate">
                        {deal.propertyaddress}
                      </span>
                      <span
                        className={`flex-shrink-0 text-[9.5px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${
                          deal.dealStage === "Under Contract"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {deal.dealStage}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-gray-500 mt-0.5 truncate">
                      {clientName} · {formatDate(deal.closingdate, "MMM D")}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold text-[13px] text-slate-900">
                      $
                      {Math.round(deal.offerPrice || 0).toLocaleString("en-US")}
                    </div>
                    <div className="text-[11.5px] text-green-700 font-semibold mt-0.5">
                      +$
                      {Math.round(commissionForDeal(deal) || 0).toLocaleString(
                        "en-US",
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="py-9 text-center text-gray-400 text-[13px]">
            <Calendar size={28} className="mx-auto text-gray-300 mb-2" />
            No upcoming closings
          </div>
        )}
      </div>
    </div>
  );
}

function MonthlyTablePanel({
  monthlyData,
  currentMonth,
  selectedUser,
  gciGoal,
  PendingDealsCount,
  closedDealsCount,
  ytdPendingVolume,
  ytdVolume,
  closedCommissionTotal,
  pendingCommissionTotal,
  TotalPct,
  filterYear,
  yearDropdownVisible,
  setYearDropdownVisible,
  setFilterYear,
  setSelectedYear,
  setstatspopup,
  setselectedstat,
  setstatsdata,
  dashboardview,
}) {
  const headers = [
    "Month",
    "Pending Units",
    "Closed Units",
    "Pending Volume",
    "Closed Volume",
    "Avg Price",
    "GCI",
    "Pending GCI",
    selectedUser?.role === "Employee" ? "GCI % of Goal" : null,
  ].filter(Boolean);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5 overflow-hidden">
      <div className="px-6 py-5 border-b border-black/[0.08] flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-[#1E293B] rounded-md inline-flex items-center justify-center flex-shrink-0 text-white">
            <BarChart3 size={14} strokeWidth={2} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900">
              Monthly Breakdown
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5">
              Aggregated by closing date · {filterYear}
            </div>
          </div>
        </div>

        {dashboardview && (
          <div className="relative">
            <button
              onClick={() => setYearDropdownVisible(!yearDropdownVisible)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
            >
              {filterYear}
            </button>
            <YearFilterDropdown
              visible={yearDropdownVisible}
              onSelect={(year) => {
                setFilterYear(year);
                setSelectedYear(Number(year));
                setYearDropdownVisible(false);
              }}
              activateOutsideClick={() => setYearDropdownVisible(false)}
              style={{ top: "40px", right: "0px" }}
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {headers.map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2.5 text-[10.5px] font-semibold text-gray-500 uppercase tracking-wider ${
                    h !== "Month" ? "text-center" : "text-left pl-1"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((m, i) => {
              const curr = i === currentMonth;
              const pct =
                gciGoal > 0
                  ? ((m.closedCommission / gciGoal) * 100).toFixed(1)
                  : 0;
              const dim = i > currentMonth + 2;
              return (
                <tr
                  key={m.month}
                  className={`border-b border-slate-100 ${
                    curr ? "bg-slate-100" : "hover:bg-gray-50"
                  } ${dim ? "opacity-55" : ""}`}
                >
                  <td className="px-3 py-3 pl-1 text-left font-semibold text-slate-900">
                    {MONTH_NAMES[m.month - 1]}
                    {curr && (
                      <span className="inline-block bg-[#1E293B] text-white text-[9px] font-bold px-1.5 py-0.5 rounded ml-2 tracking-wider">
                        NOW
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">
                    {m.PendingDealsCount > 0 ? (
                      <span
                        className="cursor-pointer text-amber-700 font-semibold hover:underline"
                        onClick={() => {
                          setstatspopup(true);
                          setselectedstat(
                            `${MONTH_NAMES[m.month - 1]} - Pending Deals`,
                          );
                          setstatsdata(m.PendingDeals);
                        }}
                      >
                        {m.PendingDealsCount}
                      </span>
                    ) : (
                      <span className="text-slate-300">0</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">
                    {m.closedDealsCount > 0 ? (
                      <span
                        className="cursor-pointer text-[#1E293B] font-semibold hover:underline"
                        onClick={() => {
                          setstatspopup(true);
                          setselectedstat(
                            `${MONTH_NAMES[m.month - 1]} - Closed Deals`,
                          );
                          setstatsdata(m.closedDeals);
                        }}
                      >
                        {m.closedDealsCount}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center text-amber-700">
                    {m.monthlypendingvolume > 0
                      ? `$${formatNumberShort(m.monthlypendingvolume)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {m.monthlyvolume > 0
                      ? `$${formatNumberShort(m.monthlyvolume)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {m.monthlyvolume > 0 && m.closedDealsCount > 0
                      ? `$${formatNumberShort(m.monthlyvolume / m.closedDealsCount)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center font-medium">
                    {m.closedCommission > 0
                      ? `$${formatNumberShort(m.closedCommission)}`
                      : <span className="text-slate-300">$0</span>}
                  </td>
                  <td className="px-3 py-3 text-center text-amber-700">
                    {m.pendingCommission > 0
                      ? `$${formatNumberShort(m.pendingCommission)}`
                      : <span className="text-slate-300">—</span>}
                  </td>
                  {selectedUser?.role === "Employee" && (
                    <td className="px-3 py-3 text-center">
                      {gciGoal > 0 ? (
                        <span className="inline-flex items-center gap-1.5 justify-center">
                          {pct}%
                          <span className="inline-block w-11 h-1 rounded-sm bg-slate-100 overflow-hidden align-middle">
                            <span
                              className="block h-full rounded-sm bg-[#1E293B]"
                              style={{
                                width: `${Math.min(Number(pct), 100)}%`,
                              }}
                            />
                          </span>
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-200">
              <td className="px-3 py-3 pl-1 text-left text-[11px] uppercase tracking-wider font-bold text-[#1E293B]">
                YTD Total
              </td>
              <td className="px-3 py-3 text-center font-bold">
                <span
                  className="cursor-pointer text-amber-700 hover:underline"
                  onClick={() => {
                    setstatspopup(true);
                    setselectedstat("Deals - Pending Deals");
                    setstatsdata(PendingDealsCount);
                  }}
                >
                  {PendingDealsCount.length}
                </span>
              </td>
              <td className="px-3 py-3 text-center font-bold">
                <span
                  className="cursor-pointer text-[#1E293B] hover:underline"
                  onClick={() => {
                    setstatspopup(true);
                    setselectedstat("Deals - Closed Deals");
                    setstatsdata(closedDealsCount);
                  }}
                >
                  {closedDealsCount.length}
                </span>
              </td>
              <td className="px-3 py-3 text-center font-bold text-amber-700">
                ${formatNumberShort(ytdPendingVolume)}
              </td>
              <td className="px-3 py-3 text-center font-bold">
                ${formatNumberShort(ytdVolume)}
              </td>
              <td className="px-3 py-3 text-center font-bold">
                {closedDealsCount.length > 0
                  ? `$${formatNumberShort(ytdVolume / closedDealsCount.length)}`
                  : "—"}
              </td>
              <td className="px-3 py-3 text-center font-bold">
                ${formatNumberShort(closedCommissionTotal)}
              </td>
              <td className="px-3 py-3 text-center font-bold text-amber-700">
                ${formatNumberShort(pendingCommissionTotal)}
              </td>
              {selectedUser?.role === "Employee" && (
                <td className="px-3 py-3 text-center font-bold">
                  {Number(TotalPct) > 0 ? `${TotalPct}%` : "—"}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default TeamPerformanceDetail;
